import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Symbol } from "./brand";

type DivProps = React.ComponentProps<"div">;

export function Label({
  className,
  accent,
  ...props
}: React.ComponentProps<"span"> & { accent?: boolean }) {
  return (
    <span
      className={cn("fx-label", accent && "text-fx-red-ink", className)}
      {...props}
    />
  );
}

const DISPLAY = {
  hero: "text-[54px] leading-[46px] tracking-[-0.045em]",
  xl: "text-[42px] leading-[36px] tracking-[-0.043em]",
  h1: "text-[28px] leading-[28px] tracking-[-0.03em] md:text-[40px] md:leading-[36px] md:tracking-[-0.035em]",
  h3: "text-[21px] leading-[22px] tracking-[-0.025em]",
  h4: "text-[19px] leading-[19px] tracking-[-0.02em]",
  h5: "text-base leading-[18px] tracking-[-0.02em]",
} as const;

export function Display({
  size = "h1",
  as: Tag = "h1",
  className,
  ...props
}: React.ComponentProps<"h1"> & {
  size?: keyof typeof DISPLAY;
  as?: "h1" | "h2" | "h3" | "span" | "div";
}) {
  const T = Tag as "h1";
  return <T className={cn("fx-display", DISPLAY[size], className)} {...props} />;
}

const NUMERAL = {
  hero: "text-[64px] leading-[52px] tracking-[-0.047em]",
  num: "text-4xl leading-[34px] tracking-[-0.04em]",
  sm: "text-[26px] leading-[26px] tracking-[-0.035em]",
  xs: "text-xl leading-5 tracking-[-0.02em]",
} as const;

export function Numeral({
  size = "num",
  cream,
  className,
  ...props
}: React.ComponentProps<"span"> & {
  size?: keyof typeof NUMERAL;
  /** Números de destaque ficam creme; os demais, brancos. */
  cream?: boolean;
}) {
  return (
    <span
      className={cn(
        "fx-num",
        cream ? "text-fx-cream" : "text-fx-title",
        NUMERAL[size],
        className,
      )}
      {...props}
    />
  );
}

export function Surface({
  focus,
  hero,
  className,
  ...props
}: DivProps & { focus?: boolean; hero?: boolean }) {
  return (
    <div
      className={cn(
        "border bg-fx-surface",
        hero ? "rounded-hero p-5 md:p-6" : "rounded-card",
        focus ? "border-fx-red-line" : "border-fx-line-subtle",
        className,
      )}
      {...props}
    />
  );
}

export function SectionHeader({
  title,
  action,
  to,
  className,
}: {
  title: string;
  action?: string;
  to?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex items-baseline justify-between", className)}>
      <h2 className="fx-display text-[19px] leading-[19px] tracking-[-0.02em]">
        {title}
      </h2>
      {action && to && (
        <Link to={to} className="text-[13px] font-semibold text-fx-cream hover:text-fx-cream-hover">
          {action}
        </Link>
      )}
    </div>
  );
}

export function ScreenTitle({
  eyebrow,
  title,
  subtitle,
  accent,
  className,
  children,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  accent?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn("flex flex-wrap items-end gap-x-6 gap-y-4", className)}>
      <div className="flex min-w-0 flex-1 basis-64 flex-col gap-2">
        {eyebrow && (
          <span className={cn("fx-label text-[10.5px] tracking-[1.5px]", accent && "text-fx-red-ink")}>
            {eyebrow}
          </span>
        )}
        <Display>{title}</Display>
        {subtitle && <p className="text-sm text-fx-muted">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function StatCell({
  label,
  value,
  cream,
  className,
}: {
  label: string;
  value: string | number;
  cream?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-1 flex-col gap-2 px-4 py-4 md:px-[22px] md:py-[18px]", className)}>
      <Label>{label}</Label>
      <Numeral cream={cream}>{value}</Numeral>
    </div>
  );
}

/** Faixa de StatCells separadas por linhas finas. */
export function StatStrip({ children, className }: DivProps) {
  return (
    <Surface
      className={cn(
        "flex [&>*+*]:border-l [&>*+*]:border-fx-line-subtle",
        className,
      )}
    >
      {children}
    </Surface>
  );
}

export function KpiCard({
  label,
  value,
  note,
  noteAccent,
}: {
  label: string;
  value: string | number;
  note?: string;
  noteAccent?: boolean;
}) {
  return (
    <Surface className="flex flex-col gap-2.5 p-[18px]">
      <Label>{label}</Label>
      <Numeral>{value}</Numeral>
      {note && (
        <span
          className={cn(
            "text-xs font-medium",
            noteAccent ? "text-fx-red-ink" : "text-fx-faint",
          )}
        >
          {note}
        </span>
      )}
    </Surface>
  );
}

export function ProgressBar({
  value,
  height = 6,
  className,
}: {
  value: number;
  height?: number;
  className?: string;
}) {
  return (
    <div
      className={cn("rounded-full bg-fx-track", className)}
      style={{ height }}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-fx-red-icon transition-[width] duration-[280ms] ease-fx"
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

export function StatusPill({
  open,
  label,
  className,
}: {
  open: boolean;
  label: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "flex h-[26px] items-center gap-1.5 rounded-full border px-2.5 text-[10px] font-bold tracking-[1.2px]",
        open
          ? "border-fx-red-border bg-fx-red-surface text-fx-red-ink"
          : "border-fx-line bg-fx-raised text-fx-muted",
        className,
      )}
    >
      {open && <span className="fx-pulse size-1.5 rounded-full bg-fx-red-icon" />}
      {label}
    </span>
  );
}

export function EmptyState({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-3 rounded-[18px] border border-dashed border-fx-line-dashed px-6 py-12 text-center",
        className,
      )}
    >
      <Symbol size={48} className="opacity-50" />
      <h2 className="fx-display text-xl leading-5 tracking-[-0.02em]">{title}</h2>
      <p className="max-w-xs text-sm text-fx-muted">{description}</p>
      {children}
    </div>
  );
}

/** Nome + valor + unidade, com barra proporcional ao maior valor da lista. */
export function BarRow({
  name,
  value,
  unit,
  percent,
  initials,
  className,
}: {
  name: string;
  value: string;
  unit?: string;
  percent: number;
  initials?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5 py-2", className)}>
      <div className="flex items-center gap-3">
        {initials !== undefined && (
          <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full border border-fx-line bg-fx-raised text-[11px] font-bold text-fx-cream">
            {initials}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-fx-body">
          {name}
        </span>
        <span className="fx-num text-base text-fx-cream">{value}</span>
        {unit && <span className="w-6 text-[11px] font-semibold text-fx-faint">{unit}</span>}
      </div>
      <div className="h-1 rounded-full bg-fx-track">
        <div
          className="h-full rounded-full bg-fx-red-icon"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
